import { Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, from, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { LocalizationService } from '@capital-access/common/localization';
import { getSelectedSecurityId } from '@capital-access/common/security';
import {
  DrawerPosition,
  DrawerType,
  FireflyDrawerService,
  FireflyModalService,
  NotificationService
} from '@capital-access/firefly/components';
import { CustomizeTargetComponent } from '../../components/customize-target/customize-target.component';
import { CUSTOM_TARGET_LOCALIZATION_SCOPE, INSTITUTION_RATING_DATA_LOAD_DELAY_MS } from '../../constants';
import { CustomTargetRepository } from '../../repositories/custom-target.repository';
import {
  confirmEditTarget,
  confirmResetRating,
  confirmResetRatingsInBulk,
  editTargetFailure,
  editTargetSuccess,
  openCustomizeTargetDrawer,
  resetRating,
  resetRatingFailure,
  resetRatingsInBulk,
  resetRatingsInBulkSuccess,
  resetRatingSuccess,
  saveEditTarget
} from './actions';

@Injectable()
export class CustomTargetEffects {
  constructor(
    private actions$: Actions,
    private localizationService: LocalizationService,
    private drawerService: FireflyDrawerService,
    private injector: Injector,
    private modal: FireflyModalService,
    private store: Store,
    private customTargetRepository: CustomTargetRepository,
    private notificationService: NotificationService
  ) {}

  openCustomizeTargetDrawer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openCustomizeTargetDrawer),
        switchMap(action =>
          this.localizationService
            .localize('customizeTargetDrawerTitle', {}, { scope: CUSTOM_TARGET_LOCALIZATION_SCOPE })
            .pipe(
              take(1),
              tap(title =>
                this.drawerService.openDrawer(
                  {
                    title,
                    position: DrawerPosition.Right,
                    type: DrawerType.Filters,
                    injector: this.injector
                  },
                  CustomizeTargetComponent,
                  { targetCompany: action.targetCompany }
                )
              )
            )
        )
      ),
    { dispatch: false }
  );

  confirmEditTarget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmEditTarget),
      switchMap(({ targetCompany, priorityRating }) =>
        this.localizationService
          .localize(
            ['confirmSaveTargetTitle', 'confirmSaveTargetBody', 'saveButton', 'cancelButton'],
            {},
            { scope: CUSTOM_TARGET_LOCALIZATION_SCOPE }
          )
          .pipe(
            take(1),
            switchMap(([title, body, actionText, dismissText]) =>
              from(
                this.modal.openConfirmation({
                  style: 'warning',
                  title,
                  body,
                  actionText,
                  dismissText
                }).result
              ).pipe(
                map(() => saveEditTarget({ targetCompany, priorityRating })),
                catchError(() => EMPTY)
              )
            )
          )
      )
    )
  );

  saveEditTarget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveEditTarget),
      withLatestFrom(this.store.select(getSelectedSecurityId)),
      switchMap(([{ targetCompany, priorityRating }, selectedSecurityId]) => {
        if (!selectedSecurityId) {
          return EMPTY;
        }
        return this.customTargetRepository
          .saveCustomTargetRating(selectedSecurityId, targetCompany.id, priorityRating)
          .pipe(
            switchMap(() => {
              return new Promise<ReturnType<typeof editTargetSuccess>>(resolve => {
                setTimeout(() => {
                  resolve(editTargetSuccess({ targetCompany }));
                }, INSTITUTION_RATING_DATA_LOAD_DELAY_MS);
              });
            }),
            catchError(() => of(editTargetFailure()))
          );
      })
    )
  );

  editTargetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(editTargetSuccess),
        switchMap(action =>
          this.localizationService
            .localize(
              ['editTargetSuccessMessage'],
              { institutionName: action.targetCompany.name },
              { scope: CUSTOM_TARGET_LOCALIZATION_SCOPE }
            )
            .pipe(
              take(1),
              tap(([message]) => this.notificationService.notify(message, { type: 'success' }))
            )
        )
      ),
    { dispatch: false }
  );

  confirmResetCustomTargetRating$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmResetRating),
      switchMap(({ targetCompany }) =>
        this.localizationService
          .localize(
            ['confirmResetRatingTitle', 'confirmResetRatingBody', 'reset', 'cancelButton'],
            {},
            { scope: CUSTOM_TARGET_LOCALIZATION_SCOPE }
          )
          .pipe(
            take(1),
            switchMap(([title, body, actionText, dismissText]) =>
              from(
                this.modal.openConfirmation({
                  style: 'warning',
                  title,
                  body,
                  actionText,
                  dismissText
                }).result
              ).pipe(
                map(() => resetRating({ targetCompany })),
                catchError(() => EMPTY)
              )
            )
          )
      )
    )
  );

  confirmResetCustomTargetRatingInBulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmResetRatingsInBulk),
      switchMap(({ selectedIds }) =>
        this.localizationService
          .localize(
            ['confirmResetRatingTitle', 'confirmResetRatingsInBulkBody', 'reset', 'cancelButton'],
            { institutionsCount: selectedIds.length },
            { scope: CUSTOM_TARGET_LOCALIZATION_SCOPE }
          )
          .pipe(
            take(1),
            switchMap(([title, body, actionText, dismissText]) =>
              from(
                this.modal.openConfirmation({
                  style: 'warning',
                  title,
                  body,
                  actionText,
                  dismissText
                }).result
              ).pipe(
                map(() => resetRatingsInBulk({ selectedIds })),
                catchError(() => EMPTY)
              )
            )
          )
      )
    )
  );

  resetRating$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetRating),
      withLatestFrom(this.store.select(getSelectedSecurityId)),
      switchMap(([{ targetCompany }, selectedSecurityId]) => {
        if (!selectedSecurityId) {
          return EMPTY;
        }
        return this.customTargetRepository.deleteCustomTargetRating(selectedSecurityId, [targetCompany.id]).pipe(
          switchMap(() => {
            return new Promise<ReturnType<typeof resetRatingSuccess>>(resolve => {
              setTimeout(() => {
                resolve(resetRatingSuccess({ targetCompany }));
              }, INSTITUTION_RATING_DATA_LOAD_DELAY_MS);
            });
          }),
          catchError(() => of(resetRatingFailure()))
        );
      })
    )
  );

  resetRatingInBulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetRatingsInBulk),
      withLatestFrom(this.store.select(getSelectedSecurityId)),
      switchMap(([{ selectedIds }, selectedSecurityId]) => {
        if (!selectedSecurityId) {
          return EMPTY;
        }
        return this.customTargetRepository.deleteCustomTargetRating(selectedSecurityId, selectedIds).pipe(
          switchMap(() => {
            return new Promise<ReturnType<typeof resetRatingsInBulkSuccess>>(resolve => {
              setTimeout(() => {
                resolve(resetRatingsInBulkSuccess({ selectedIds }));
              }, INSTITUTION_RATING_DATA_LOAD_DELAY_MS);
            });
          }),
          catchError(() => of(resetRatingFailure()))
        );
      })
    )
  );

  resetRatingSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetRatingSuccess),
        switchMap(action =>
          this.localizationService
            .localize(
              ['resetSuccessAlert'],
              { institutionName: action.targetCompany.name },
              { scope: CUSTOM_TARGET_LOCALIZATION_SCOPE }
            )
            .pipe(
              take(1),
              tap(([message]) => this.notificationService.notify(message, { type: 'success' }))
            )
        )
      ),
    { dispatch: false }
  );

  resetRatingsInBulkSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetRatingsInBulkSuccess),
        switchMap(action =>
          this.localizationService
            .localize(
              ['resetSuccessAlertBulk'],
              { institutionsCount: action.selectedIds.length },
              { scope: CUSTOM_TARGET_LOCALIZATION_SCOPE }
            )
            .pipe(
              take(1),
              tap(([message]) => this.notificationService.notify(message, { type: 'success' }))
            )
        )
      ),
    { dispatch: false }
  );

  closePopoversOnDrawerOpen$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openCustomizeTargetDrawer),
        tap(() => {
          const popovers = document.querySelectorAll('.popover');
          popovers.forEach(popover => popover.remove());
        })
      ),
    { dispatch: false }
  );
}
